import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import axios from 'axios';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getAdminCourseName } from '../utils/courseDisplay';

interface ScormConnection {
  id: string;
  student: {
    id: string;
    firstName: string;
    lastName: string;
    email?: string;
  } | null;
  lmsUserId: string;
  firstConnectedAt: string;
  lastConnectedAt: string;
  connectionCount: number;
}

interface ScormConnectionsData {
  course: {
    id: string;
    courseProject?: string;
    title: string | Record<string, string>;
    defaultLanguage?: string;
  };
  export: {
    id: string;
    clientId: string;
    scormVersion: '1.2' | '2004';
    active: boolean;
    tokenExpiresAt?: string | null;
    exportVersion: string;
    exportedAt: string;
    company?: {
      id: string;
      name: string;
      slug: string;
    } | null;
    freeEnrollment?: boolean;
    emailOptional?: boolean;
    enrollmentGroup?: {
      id: string;
      name: string;
    } | null;
  };
  connections: ScormConnection[];
}

const ScormExportConnections: React.FC = () => {
  const { courseId, exportId } = useParams<{ courseId: string; exportId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState<ScormConnectionsData | null>(null);

  const token = localStorage.getItem('admin_token');
  const apiBaseUrl = process.env.REACT_APP_API_URL;

  useEffect(() => {
    const fetchConnections = async () => {
      if (!courseId || !exportId) return;
      setLoading(true);
      setError('');
      try {
        const response = await axios.get(`${apiBaseUrl}/scorm/courses/${courseId}/exports/${exportId}/connections`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(response.data);
      } catch (err: any) {
        setError(err?.response?.data?.error || "Impossible de charger les connexions SCORM");
      } finally {
        setLoading(false);
      }
    };

    void fetchConnections();
  }, [apiBaseUrl, courseId, exportId, token]);

  const courseTitle = useMemo(() => {
    if (!data) return '';
    return getAdminCourseName(data.course);
  }, [data]);

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <LinearProgress />
      </Container>
    );
  }

  if (error || !data) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">{error || 'Aucune donnée disponible'}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Connexions SCORM - {data.export.clientId}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Formation: {courseTitle}
          </Typography>
        </Box>
        <Button variant="outlined" onClick={() => navigate(`/courses/${courseId}/scorm`)}>
          Retour exports SCORM
        </Button>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Donnees du package</Typography>
          <Box display="flex" flexWrap="wrap" gap={1}>
            <Chip label={`Client ID: ${data.export.clientId}`} />
            <Chip label={`Version export: ${data.export.exportVersion}`} />
            <Chip label={`SCORM ${data.export.scormVersion}`} />
            <Chip color={data.export.active ? 'success' : 'default'} label={data.export.active ? 'Actif' : 'Inactif'} />
            <Chip label={`Entreprise: ${data.export.company?.name || 'Non assignée'}`} />
            <Chip label={`Expire: ${data.export.tokenExpiresAt ? new Date(data.export.tokenExpiresAt).toLocaleString() : 'Jamais'}`} />
            {data.export.freeEnrollment && (
              <Chip color="info" variant="outlined" label="Inscription libre" />
            )}
            {data.export.freeEnrollment && data.export.emailOptional && (
              <Chip color="info" variant="outlined" label="Email optionnel" />
            )}
            {data.export.enrollmentGroup ? (
              <Chip
                color="primary"
                variant="outlined"
                label={`Groupe d'inscription : ${data.export.enrollmentGroup.name}`}
              />
            ) : (
              data.export.freeEnrollment && (
                <Chip color="warning" variant="outlined" label="Aucun groupe d'inscription configuré" />
              )
            )}
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Connexions ({data.connections.length})
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Apprenant</TableCell>
                  <TableCell>LMS User ID</TableCell>
                  <TableCell>Premiere connexion</TableCell>
                  <TableCell>Derniere connexion</TableCell>
                  <TableCell>Nb connexions</TableCell>
                  <TableCell align="right">Tracking</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.connections.map((connection) => (
                  <TableRow key={connection.id}>
                    <TableCell>
                      {connection.student
                        ? `${connection.student.firstName} ${connection.student.lastName}${connection.student.email ? ` (${connection.student.email})` : ''}`
                        : 'Apprenant introuvable'}
                    </TableCell>
                    <TableCell>{connection.lmsUserId}</TableCell>
                    <TableCell>{new Date(connection.firstConnectedAt).toLocaleString()}</TableCell>
                    <TableCell>{new Date(connection.lastConnectedAt).toLocaleString()}</TableCell>
                    <TableCell>{connection.connectionCount}</TableCell>
                    <TableCell align="right">
                      {connection.student ? (
                        <Button
                          size="small"
                          endIcon={<OpenInNewIcon fontSize="small" />}
                          onClick={() => navigate(`/courses/${courseId}/students/${connection.student?.id}/detail`)}
                        >
                          Ouvrir
                        </Button>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Container>
  );
};

export default ScormExportConnections;
